import React, { useCallback, useMemo, useState } from 'react';
import PasswordStrengthBar from 'react-password-strength-bar';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import {
  Container,
  Label,
  StyledInput,
  ErrorMessage,
  VisibilityButton,
  VisibilityButtonIcon,
} from '@components/Form/Input/styles';
import EyeIcon from '@assets/images/icons/eye.svg';
import EyeOffIcon from '@assets/images/icons/eye-off.svg';

type Props = {
  label: string;
  onChange: (value: string) => void;
  value: string;
  name: string;
  error?: {
    message: string;
  };
  autoComplete?: string;
  showStrength?: boolean;
};

function PasswordInput({ label, onChange, value, name, error, autoComplete, showStrength }: Props) {
  const { t } = useTranslation('register');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const changePasswordVisibility = useCallback(() => {
    setPasswordVisible(!passwordVisible);
  }, [passwordVisible]);

  const inputId = useMemo(() => `input_${name}-passwd`, [name]);
  return (
    <Container showStrength={showStrength && !!value}>
      <Label htmlFor={inputId}>{label}</Label>
      <StyledInput
        {...(autoComplete ? { autoComplete } : {})}
        id={inputId}
        type={passwordVisible ? 'text' : 'password'}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        className={error && 'invalid'}
      />
      <VisibilityButton type="button" onClick={changePasswordVisibility}>
        <VisibilityButtonIcon visible={!passwordVisible}>
          <Image src={EyeIcon} alt="" />
        </VisibilityButtonIcon>
        <VisibilityButtonIcon visible={passwordVisible}>
          <Image src={EyeOffIcon} alt="" />
        </VisibilityButtonIcon>
      </VisibilityButton>
      {showStrength && !!value ? (
        <PasswordStrengthBar
          password={value}
          scoreWords={[t('Weak'), t('Weak'), t('Okay'), t('Good'), t('Strong')]}
          shortScoreWord={t('Too short')}
        />
      ) : null}
      {error && <ErrorMessage showStrength={showStrength && !!value}>{error.message}</ErrorMessage>}
    </Container>
  );
}

PasswordInput.defaultProps = {
  error: undefined,
  autoComplete: null,
  showStrength: false,
};

export default PasswordInput;
